import React, { ReactNode } from 'react';
import classNames from 'classnames';

import styles from './MultiSelectButton.module.css';

export interface MultiSelectButtonOption<T> {
  label: string | ReactNode;
  value: T;
  disabledOption?: boolean;
}

interface MultiSelectButtonBaseProps<T> {
  dataHcName: string;
  options: MultiSelectButtonOption<T>[];
  disabled?: boolean;
  className?: string;
  label?: ReactNode;
}

export interface MultiSelectButtonPropsMulti<T>
  extends MultiSelectButtonBaseProps<T> {
  singleSelect?: false;
  value?: T[] | null;
  onChange: (value: T[]) => void;
}

export interface MultiSelectButtonPropsSingle<T>
  extends MultiSelectButtonBaseProps<T> {
  singleSelect: true;
  value?: T | null;
  onChange: (value: T | null) => void;
}

export type MultiSelectButtonProps<T> =
  | MultiSelectButtonPropsMulti<T>
  | MultiSelectButtonPropsSingle<T>;

export const MultiSelectButton = <T extends number | string | boolean | null>({
  dataHcName,
  options,
  disabled,
  className,
  label,
  ...restProps
}: MultiSelectButtonProps<T>) => {
  const handleMultiChange = (
    v: T,
    currentValue: T[],
    onChange: (value: T[]) => void,
  ) => {
    const updated = [...currentValue];
    const idx = updated.indexOf(v);
    if (idx > -1) {
      updated.splice(idx, 1);
    } else {
      updated.push(v);
    }
    onChange(updated);
  };

  const isSelected = (optionValue: T): boolean => {
    if (restProps.singleSelect) {
      return restProps.value === optionValue;
    }
    return (restProps.value ?? []).indexOf(optionValue) > -1;
  };

  const handleClick = (optionValue: T) => {
    if (restProps.singleSelect) {
      const newValue = restProps.value === optionValue ? null : optionValue;
      restProps.onChange(newValue);
    } else {
      handleMultiChange(optionValue, restProps.value ?? [], restProps.onChange);
    }
  };

  return (
    <div className={classNames(styles.MultiSelectButton, className)}>
      {label}
      <div className={styles.OptionsRow}>
        {options.map((option) => {
          const selected = isSelected(option.value);
          const isDisabled = disabled || option.disabledOption;
          return (
            <button
              key={`option-${option.value}`}
              data-hc-name={dataHcName}
              className={classNames(
                styles.Option,
                selected && styles.selected,
                isDisabled && styles.disabled,
              )}
              disabled={isDisabled}
              aria-pressed={selected}
              type="button"
              onClick={() => handleClick(option.value)}
            >
              <span
                className={styles.OptionLabel}
                title={
                  typeof option.label === 'string' ? option.label : undefined
                }
              >
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
